import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  GitBranch, 
  Layers, 
  Boxes, 
  Info, 
  Lightbulb, 
  AlertTriangle, 
  Bookmark, 
  Bold, 
  Italic, 
  Heading2, 
  Heading3, 
  List, 
  ListOrdered, 
  Link as LinkIcon, 
  Sparkles,
  ChevronDown,
  HelpCircle,
  PlusCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

interface ArticleEditorToolbarProps {
  content: string;
  onChange: (newContent: string) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
}

export default function ArticleEditorToolbar({ content, onChange, textareaRef }: ArticleEditorToolbarProps) {
  const insertText = (before: string, after: string = '', defaultText: string = '') => {
    const textarea = textareaRef?.current;
    if (!textarea) {
      // Fallback: append at end
      const addition = before + defaultText + after;
      onChange(content ? `${content}\n\n${addition}` : addition);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.substring(start, end) || defaultText;
    const replacement = before + selected + after;

    const newContent = content.substring(0, start) + replacement + content.substring(end);
    onChange(newContent);

    // Reposition cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selected.length
      );
    }, 10);
  };

  const insertBlock = (template: string) => {
    const textarea = textareaRef?.current;
    if (!textarea) {
      onChange(content ? `${content}\n\n${template}` : template);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Check if we need leading/trailing newlines
    const beforeCursor = content.substring(0, start);
    const afterCursor = content.substring(end);
    
    let prefix = '';
    if (beforeCursor.length > 0 && !beforeCursor.endsWith('\n\n')) {
      prefix = beforeCursor.endsWith('\n') ? '\n' : '\n\n';
    }

    let suffix = '';
    if (afterCursor.length > 0 && !afterCursor.startsWith('\n\n')) {
      suffix = afterCursor.startsWith('\n') ? '\n' : '\n\n';
    }

    const replacement = `${prefix}${template}${suffix}`;
    const newContent = beforeCursor + replacement + afterCursor;
    onChange(newContent);

    setTimeout(() => {
      textarea.focus();
      const newPos = start + prefix.length + template.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 10);
  };

  return (
    <div className="bg-card border border-border p-2 rounded-2xl flex flex-wrap items-center gap-1.5 shadow-sm mb-3">
      {/* Group: Core Structure Elements (Table & Boxes) */}
      <div className="flex items-center gap-1 bg-muted/60 p-1 rounded-xl">
        {/* Table Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              className="h-8 gap-1.5 font-bold text-xs bg-gold/15 border-gold/40 text-navy dark:text-gold hover:bg-gold/25"
            >
              <Table className="h-3.5 w-3.5 text-gold" />
              <span>Tabel</span>
              <ChevronDown className="h-3 w-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 p-2 bg-card border-border shadow-xl">
            <DropdownMenuLabel className="text-[11px] font-bold text-foreground/60 uppercase tracking-wider">
              Pilihan Format Tabel
            </DropdownMenuLabel>
            <DropdownMenuItem 
              className="cursor-pointer py-2"
              onClick={() => insertBlock(`| No | Posisi / Komponen | Uraian Tugas & Spesifikasi | Keterangan / Syarat |
|:--:|:------------------|:---------------------------|:--------------------|
| 1  | Project Manager   | Memimpin seluruh operasional proyek | SKK Jenjang 8 / S1 Sipil |
| 2  | Site Manager      | Pengawasan teknis lapangan & mutu  | SKK Jenjang 7 / S1 Sipil |
| 3  | Ahli K3 Konstruksi| Penerapan sistem keselamatan SMKK  | Sertifikat Ahli K3 |
| 4  | Quantity Surveyor | Perhitungan volume & progres fisik | S1 / D3 Teknik Sipil |`)}
            >
              <div>
                <p className="font-bold text-xs text-foreground">Tabel Tim / Personil (4 Kolom)</p>
                <p className="text-[10px] text-foreground/50">Cocok untuk susunan pelaksana proyek</p>
              </div>
            </DropdownMenuItem>

            <DropdownMenuItem 
              className="cursor-pointer py-2"
              onClick={() => insertBlock(`| No | Uraian Pekerjaan | Satuan | Koefisien | Harga Satuan (Rp) |
|:--:|:-----------------|:------:|:---------:|:-----------------:|
| 1  | Pekerja          |  OH    |   0.050   |     120.000       |
| 2  | Mandor           |  OH    |   0.010   |     160.000       |
| 3  | Semen Portland   |  kg    |   8.000   |       1.850       |
| 4  | Pasir Pasang     |  m3    |   0.015   |     250.000       |`)}
            >
              <div>
                <p className="font-bold text-xs text-foreground">Tabel Rincian Biaya / AHSP (5 Kolom)</p>
                <p className="text-[10px] text-foreground/50">Tabel koefisien material & tenaga kerja</p>
              </div>
            </DropdownMenuItem>

            <DropdownMenuItem 
              className="cursor-pointer py-2"
              onClick={() => insertBlock(`| Kriteria / Fitur | Metode Konvensional | Metode Modern / Modular |
|:-----------------|:---------------------|:-------------------------|
| Waktu Pengerjaan | 6 - 8 Bulan          | 3 - 4 Bulan (Lebih Cepat)|
| Kebutuhan Tenaga | Relatif Banyak       | Lebih Ringkas & Presisi  |
| Pengendalian Mutu| Dipengaruhi Cuaca    | Pabrikasi Terkontrol     |`)}
            >
              <div>
                <p className="font-bold text-xs text-foreground">Tabel Perbandingan (3 Kolom)</p>
                <p className="text-[10px] text-foreground/50">Komparasi metode, biaya, atau spesifikasi</p>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Structure Diagram Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              className="h-8 gap-1.5 font-bold text-xs bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
            >
              <GitBranch className="h-3.5 w-3.5" />
              <span>Bagan Struktur</span>
              <ChevronDown className="h-3 w-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72 p-2 bg-card border-border shadow-xl">
            <DropdownMenuLabel className="text-[11px] font-bold text-foreground/60 uppercase tracking-wider">
              Template Struktur & Hirarki
            </DropdownMenuLabel>
            <DropdownMenuItem 
              className="cursor-pointer py-2"
              onClick={() => insertBlock(`\`\`\`structure
title: Struktur Organisasi Pelaksana Proyek
[Direktur Utama / Kontraktor] | Penanggung Jawab Tertinggi
  -> [Project Manager (PM)] | Pimpinan Operasional Lapangan
    -> [Site Manager / Pelaksana Sipil] | Kontrol Fisik & Progres
      - Mandor Pembesian
      - Mandor Bekisting
      - Mandor Pengecoran Beton
    -> [Ahli K3 Konstruksi (SMKK)] | Pengawas Keselamatan Kerja
      - Petugas Safety (HSE)
      - Tim Tanggap Darurat & P3K
    -> [Quality Control & Surveyor] | Pengendalian Mutu & Ukur
\`\`\``)}
            >
              <div>
                <p className="font-bold text-xs text-foreground">Struktur Organisasi Proyek (Hirarki)</p>
                <p className="text-[10px] text-foreground/50">Bagan Direktur -&gt; PM -&gt; SM -&gt; Mandor</p>
              </div>
            </DropdownMenuItem>

            <DropdownMenuItem 
              className="cursor-pointer py-2"
              onClick={() => insertBlock(`\`\`\`structure
title: Unit Pengelola SMKK (Keselamatan Konstruksi)
[Penanggung Jawab K3 Konstruksi] | Komitmen Manajemen
  -> [Koordinator K3 Lapangan] | Pengawasan Harian
    -> [Petugas Inspeksi APD & Peralatan]
    -> [Petugas P3K & Tanggap Darurat]
    -> [Pengawas Lingkungan & Limbah Proyek]
\`\`\``)}
            >
              <div>
                <p className="font-bold text-xs text-foreground">Struktur Tim K3 / SMKK</p>
                <p className="text-[10px] text-foreground/50">Sesuai Permen PUPR No. 10 Tahun 2021</p>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Workflow / Steps Button */}
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          className="h-8 gap-1.5 font-bold text-xs bg-muted border-border hover:bg-muted/80 text-foreground"
          onClick={() => insertBlock(`\`\`\`alur
title: Alur Pengajuan Dokumen Tender & Penawaran
1. Persiapan Dokumen Kualifikasi | Verifikasi izin usaha, SBU, SKN, dan SPT Tahunan.
2. Perhitungan Analisa Harga Satuan (AHSP) | Menghitung harga bahan, upah, dan alat secara akurat.
3. Penyusunan Rencana Keselamatan Konstruksi (RKK) | Identifikasi bahaya kerja & mitigasi risiko SMKK.
4. Finalisasi Berkas Penawaran | Enkripsi dokumen teknis & harga sebelum submit ke LPSE/SPSE.
\`\`\``)}
        >
          <Layers className="h-3.5 w-3.5 text-primary" />
          <span>Alur Tahapan</span>
        </Button>

        {/* Multi-Column Card Grid */}
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          className="h-8 gap-1.5 font-bold text-xs bg-muted border-border hover:bg-muted/80 text-foreground"
          onClick={() => insertBlock(`\`\`\`kotak
Poin Kunci 1: Analisa AHSP Terstandar
Pastikan selalu menggunakan pedoman Permen PUPR terbaru agar harga satuan lolos evaluasi kewajaran tender.
---
Poin Kunci 2: Penerapan SMKK Disiplin
Dokumen Rencana Keselamatan Konstruksi (RKK) wajib ditandatangani oleh pimpinan badan usaha dan Ahli K3 bersertifikat.
\`\`\``)}
        >
          <Boxes className="h-3.5 w-3.5 text-gold" />
          <span>Kotak Grid</span>
        </Button>
      </div>

      {/* Group: Callout Highlight Boxes */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            type="button" 
            variant="outline" 
            size="sm" 
            className="h-8 gap-1 font-bold text-xs border-dashed border-gold/40 text-foreground hover:bg-gold/10"
          >
            <Sparkles className="h-3.5 w-3.5 text-gold" />
            <span>Kotak Sorotan</span>
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 p-2 bg-card border-border shadow-xl">
          <DropdownMenuItem 
            className="cursor-pointer py-2"
            onClick={() => insertBlock(`> [!INFO]
> Masukkan catatan penting atau informasi tambahan yang perlu diketahui pembaca di sini.`)}
          >
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-xs text-foreground">Kotak Informasi (Biru)</p>
                <p className="text-[10px] text-foreground/50">Catatan edukasi & pengingat</p>
              </div>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem 
            className="cursor-pointer py-2"
            onClick={() => insertBlock(`> [!TIPS]
> Bagikan tips praktis atau saran profesional untuk meningkatkan efisiensi pengerjaan di sini.`)}
          >
            <div className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-xs text-foreground">Kotak Tips Sukses (Hijau)</p>
                <p className="text-[10px] text-foreground/50">Rekomendasi & best practice</p>
              </div>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem 
            className="cursor-pointer py-2"
            onClick={() => insertBlock(`> [!PERINGATAN]
> Perhatikan klausul kontrak atau sanksi keterlambatan pekerjaan sesuai regulasi perundangan yang berlaku.`)}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-xs text-foreground">Kotak Peringatan (Kuning/Oranye)</p>
                <p className="text-[10px] text-foreground/50">Peringatan risiko atau regulasi kritis</p>
              </div>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem 
            className="cursor-pointer py-2"
            onClick={() => insertBlock(`> [!PENTING]
> Dokumen ini merupakan persyaratan mutlak dalam evaluasi tender dan tidak boleh diabaikan.`)}
          >
            <div className="flex items-start gap-2">
              <Bookmark className="h-4 w-4 text-gold shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-xs text-foreground">Kotak Sorotan Emas (Khas AZMAN204)</p>
                <p className="text-[10px] text-foreground/50">Highlight nilai tertinggi</p>
              </div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="h-4 w-px bg-border mx-1" />

      {/* Group: Markdown Formatting */}
      <div className="flex items-center gap-0.5">
        <Button 
          type="button" 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-foreground/70 hover:text-foreground hover:bg-muted" 
          title="Tebal (Bold)"
          onClick={() => insertText('**', '**', 'teks tebal')}
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-foreground/70 hover:text-foreground hover:bg-muted" 
          title="Miring (Italic)"
          onClick={() => insertText('*', '*', 'teks miring')}
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-foreground/70 hover:text-foreground hover:bg-muted font-bold text-xs" 
          title="Heading 2 (Judul Bagian)"
          onClick={() => insertBlock('## Judul Bagian Baru')}
        >
          <Heading2 className="h-3.5 w-3.5" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-foreground/70 hover:text-foreground hover:bg-muted font-bold text-xs" 
          title="Heading 3 (Sub-Judul)"
          onClick={() => insertBlock('### Sub-Judul Rincian')}
        >
          <Heading3 className="h-3.5 w-3.5" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-foreground/70 hover:text-foreground hover:bg-muted" 
          title="Daftar Poin (Bullet List)"
          onClick={() => insertBlock('- Poin pertama\n- Poin kedua\n- Poin ketiga')}
        >
          <List className="h-3.5 w-3.5" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-foreground/70 hover:text-foreground hover:bg-muted" 
          title="Daftar Nomor (Numbered List)"
          onClick={() => insertBlock('1. Langkah pertama\n2. Langkah kedua\n3. Langkah ketiga')}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </Button>
        <Button 
          type="button" 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-foreground/70 hover:text-foreground hover:bg-muted" 
          title="Sisipkan Tautan (Link)"
          onClick={() => insertText('[', '](https://...)', 'Nama Tautan')}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
